import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CustomButton from "components/common/Button";
import { hasPermission } from "hooks/usePermission";

import {
  getBillingCustomer,
  getBillingCustomerCreditBalance,
  getBillingCustomerDunning,
  getBillingCustomerPaymentMethods,
  getBillingCustomerPayments,
  listBillingCharges,
  listBillingInvoices,
  listBillingSubscriptions,
} from "services/billing";

import { buildChargeColumns } from "./ChargesTab";
import InvoiceStatusChip from "./InvoiceStatusChip";
import RetryChargeModal from "./RetryChargeModal";
import SubscriptionStatusModal from "./SubscriptionStatusModal";
import { CustomChip, TableComponent } from "./ui";
import {
  BsCharge,
  BsCustomer,
  BsInvoice,
  BsPaymentMethod,
  BsSubscription,
  formatBillingSchedule,
  formatDate,
  formatMoney,
  formatNextCharge,
  formatOrdinalDay,
  statusColor,
  titleCase,
} from "./types";

const subscriptionColumns = [
  { id: "planName", label: "Plan" },
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "amountCents", label: "Amount", render: (v: number, row: BsSubscription) => formatMoney(v, row.currency) },
  { id: "nextBillingDate", label: "Next billing", render: (v: string) => formatDate(v) },
];

const invoiceColumns = [
  {
    id: "status",
    label: "Status",
    render: (v: string, row: BsInvoice) => <InvoiceStatusChip status={v} metadata={row.metadata} />,
  },
  { id: "type", label: "Type", render: (v: string) => titleCase(v) },
  { id: "totalAmountCents", label: "Amount", render: (v: number, row: BsInvoice) => formatMoney(v, row.currency) },
  { id: "dueDate", label: "Due", render: (v: string) => formatDate(v) },
  { id: "paidAt", label: "Paid", render: (v: string) => formatDate(v) },
];

const paymentColumns = [
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "amountCents", label: "Amount", render: (v: number) => formatMoney(v) },
  { id: "paymentMethodType", label: "Method", render: (v: string) => titleCase(v) },
  { id: "attemptNumber", label: "Attempt" },
  // [PRD-3791] When the payment actually happened (Stripe/monolith time for migrated
  // charges), not the migration day.
  {
    id: "chargedAt",
    label: "Charged on",
    render: (v: string | null | undefined, row: Record<string, unknown>) =>
      formatDate(v || (row.createdAt as string | undefined)),
  },
  { id: "failureReason", label: "Failure", render: (v: string) => v || "—" },
];

const dunningColumns = [
  { id: "attemptNumber", label: "Attempt" },
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  { id: "scheduledDate", label: "Scheduled", render: (v: string) => formatDate(v) },
  { id: "executedAt", label: "Executed", render: (v: string) => formatDate(v) },
  { id: "failureReason", label: "Failure", render: (v: string) => v || "—" },
];

const mandateColumns = [
  { id: "mandateId", label: "Mandate ID" },
  { id: "method", label: "Payment method" },
  {
    id: "isDefault",
    label: "Default",
    render: (v: boolean) => (v ? <CustomChip label="Default" size="small" variant="filled" /> : "—"),
  },
  { id: "status", label: "Status", render: (v: string) => titleCase(v) },
];

const paymentMethodColumns = [
  { id: "type", label: "Type", render: (v: string) => titleCase(v) },
  {
    id: "brand",
    label: "Detail",
    render: (_v: string, row: BsPaymentMethod) =>
      `${row.brand || row.bankName || "—"} •••• ${row.lastFour || "----"}`,
  },
  {
    id: "isDefault",
    label: "Default",
    render: (v: boolean) => (v ? <CustomChip label="Default" size="small" variant="filled" /> : "—"),
  },
  { id: "status", label: "Status", render: (v: string) => titleCase(v) },
];

const TABS = [
  { key: "invoices", label: "Invoices" },
  { key: "payments", label: "Payments" },
  { key: "charges", label: "Charges" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "paymentMethods", label: "Payment methods" },
  { key: "mandates", label: "Mandates" },
  { key: "dunning", label: "Dunning" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-025" style={{ minWidth: 220 }}>
      <p className="para-body-xs-medium color-text-body-secondary" style={{ textTransform: "uppercase" }}>
        {label}
      </p>
      <p className="para-body-m-medium color-text-body-primary">{value}</p>
    </div>
  );
}

export default function CustomerDetailModal({
  customer,
  onClose,
}: {
  customer: BsCustomer;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<TabKey>("invoices");
  const [subscriptions, setSubscriptions] = useState<BsSubscription[]>([]);
  const [invoices, setInvoices] = useState<BsInvoice[]>([]);
  const [charges, setCharges] = useState<BsCharge[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<BsPaymentMethod[]>([]);
  const [creditBalance, setCreditBalance] = useState<{ balanceCents: number; currency: string } | null>(null);
  const [payments, setPayments] = useState<Record<string, unknown>[]>([]);
  const [dunning, setDunning] = useState<Record<string, unknown>[]>([]);
  const [selectedSub, setSelectedSub] = useState<BsSubscription | null>(null);
  const [retryCharge, setRetryCharge] = useState<BsCharge | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // [PRD-3791] Same columns as the Charges tab (Retry, Attempts, Invoice date, Charged on,
  // Failure), minus Customer.
  const canRetry = hasPermission("billing.manage");
  const chargeColumns = useMemo(() => {
    const maxAttemptByInvoice: Record<string, number> = {};
    for (const c of charges) {
      maxAttemptByInvoice[c.invoiceId] = Math.max(maxAttemptByInvoice[c.invoiceId] ?? 0, c.attemptNumber);
    }
    return buildChargeColumns(canRetry, setRetryCharge, maxAttemptByInvoice, { showCustomer: false });
  }, [canRetry, charges]);
  // [PRD-3767] Prepaid/postpaid + charge day come from BS. Search rows may not
  // carry them, so fetch the customer and fall back to the row's values.
  const [billingTerms, setBillingTerms] = useState<{ isPrepaid?: boolean; chargeDay?: number }>({});
  const canManage = hasPermission("billing.manage");

  // [PRD-3767] Own effect (not in the Promise.all below) so the header fills fast.
  // The service returns the raw payload even on HTTP errors, so only accept typed values.
  useEffect(() => {
    let active = true;
    setBillingTerms({});
    (async () => {
      const bs = await getBillingCustomer(customer.id).catch(() => null);
      if (!active) return;
      setBillingTerms({
        isPrepaid: typeof bs?.isPrepaid === "boolean" ? bs.isPrepaid : undefined,
        chargeDay: typeof bs?.chargeDay === "number" ? bs.chargeDay : undefined,
      });
    })();
    return () => {
      active = false;
    };
  }, [customer.id]);

  useEffect(() => {
    let active = true;
    (async () => {
      const [subs, invs, chgs, pms, credit, pays, dun] = await Promise.all([
        listBillingSubscriptions({ customerId: customer.id, limit: 100 }).catch(() => ({ data: [] })),
        listBillingInvoices({ customerId: customer.id, limit: 100 }).catch(() => ({ data: [] })),
        // [PRD-3791] Same view as the Charges tab: one row per invoice (its latest attempt).
        listBillingCharges({ customerId: customer.id, latestOnly: true, limit: 100 }).catch(() => ({ data: [] })),
        getBillingCustomerPaymentMethods(customer.id).catch(() => ({ paymentMethods: [] })),
        getBillingCustomerCreditBalance(customer.id).catch(() => null),
        getBillingCustomerPayments(customer.id, { limit: 100 }).catch(() => ({ data: [] })),
        getBillingCustomerDunning(customer.id, { limit: 100 }).catch(() => ({ data: [] })),
      ]);
      if (!active) return;
      setSubscriptions(subs?.data ?? []);
      setInvoices(invs?.data ?? []);
      setCharges(chgs?.data ?? []);
      setPaymentMethods(pms?.paymentMethods ?? []);
      setCreditBalance(credit);
      setPayments(pays?.data ?? []);
      setDunning(dun?.data ?? []);
    })();
    return () => {
      active = false;
    };
  }, [customer.id, refreshTick]);

  const defaultPm = paymentMethods.find((pm) => pm.isDefault);
  const defaultPmLabel = defaultPm
    ? `${titleCase(defaultPm.type)} — ${defaultPm.bankName || defaultPm.brand || ""} •••• ${defaultPm.lastFour || "----"}`
    : "—";
  const balanceLabel =
    creditBalance && creditBalance.balanceCents
      ? formatMoney(creditBalance.balanceCents, creditBalance.currency)
      : "No credit";

  // [PRD-3767] Never assume prepaid: unknown stays unknown and renders no chip.
  const isPrepaid =
    typeof billingTerms.isPrepaid === "boolean"
      ? billingTerms.isPrepaid
      : typeof customer.isPrepaid === "boolean"
        ? customer.isPrepaid
        : undefined;
  const chargeDay =
    typeof billingTerms.chargeDay === "number"
      ? billingTerms.chargeDay
      : typeof customer.chargeDay === "number"
        ? customer.chargeDay
        : undefined;

  // Mandates aren't a separate BS resource — they live on a payment method's
  // metadata as { mandate_id }. Derive one row per PM that carries a mandate.
  const mandates = paymentMethods
    .filter((pm) => pm.metadata?.mandate_id)
    .map((pm) => ({
      id: pm.id,
      mandateId: String((pm.metadata as Record<string, unknown>).mandate_id),
      method: `${pm.bankName || pm.brand || titleCase(pm.type)} •••• ${pm.lastFour || "----"}`,
      isDefault: pm.isDefault,
      status: pm.status,
    }));

  // Add a Pause/Resume action to the subscriptions table when the operator can manage billing.
  const subscriptionCols = canManage
    ? [
        ...subscriptionColumns,
        {
          id: "actions",
          label: "Actions",
          render: (_v: unknown, row: BsSubscription) =>
            row.status === "active" || row.status === "paused" ? (
              <CustomButton
                label={row.status === "active" ? "Pause" : "Resume"}
                onClick={() => setSelectedSub(row)}
              />
            ) : (
              "—"
            ),
        },
      ]
    : subscriptionColumns;

  return (
    <Dialog
      open
      // Only the ✕ button closes this modal — ignore backdrop clicks and Escape.
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      maxWidth="xl"
      fullWidth
      // MUI v9 ignores PaperProps — size the paper via the class (matches the app's other dialogs).
      sx={{
        "& .MuiDialog-paper": {
          height: "90vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-025">
            <div className="flex items-center gap-05">
              <p className="heading-h3-semibold color-text-heading-primary">{customer.name}</p>
              {isPrepaid !== undefined && (
                <CustomChip label={isPrepaid ? "Prepaid" : "Postpaid"} size="small" variant="outlined" color="default" />
              )}
            </div>
            <p className="para-body-s-regular color-text-body-secondary">{customer.email}</p>
          </div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, overflowY: "auto" }}>
        {/* Header info card */}
        <div className="border rounded-lg p-1 flex flex-wrap gap-1" style={{ rowGap: 20 }}>
          <Field label="Customer since" value={formatDate(customer.createdAt)} />
          <Field label="Credit balance" value={balanceLabel} />
          <Field label="Charge day" value={formatOrdinalDay(chargeDay)} />
          <Field label="Billing schedule" value={formatBillingSchedule(isPrepaid, chargeDay)} />
          <Field label="Next charge" value={formatNextCharge(subscriptions)} />
          <Field label="Status" value={titleCase(customer.status)} />
          <Field label="Default payment method" value={defaultPmLabel} />
          <Field label="Billing customer ID" value={customer.id} />
          <Field label="Monolith customer ID" value={customer.monolithCustomerId} />
        </div>

        {/* Tab bar */}
        <div className="flex gap-05 mt-1 mb-1" style={{ flexWrap: "wrap" }}>
          {TABS.map((t) => (
            <CustomChip
              key={t.key}
              label={t.label}
              size="small"
              onClick={() => setTab(t.key)}
              variant={tab === t.key ? "filled" : "outlined"}
              color={tab === t.key ? "primary" : "default"}
            />
          ))}
        </div>

        {/* Tab content */}
        {tab === "invoices" && <TableComponent columns={invoiceColumns} data={invoices} disableSort tableCellSmall />}
        {tab === "payments" && <TableComponent columns={paymentColumns} data={payments} disableSort tableCellSmall />}
        {tab === "charges" && <TableComponent columns={chargeColumns} data={charges} disableSort tableCellSmall />}
        {tab === "subscriptions" && (
          <TableComponent columns={subscriptionCols} data={subscriptions} disableSort tableCellSmall />
        )}
        {tab === "paymentMethods" && (
          <TableComponent columns={paymentMethodColumns} data={paymentMethods} disableSort tableCellSmall />
        )}
        {tab === "mandates" && <TableComponent columns={mandateColumns} data={mandates} disableSort tableCellSmall />}
        {tab === "dunning" && <TableComponent columns={dunningColumns} data={dunning} disableSort tableCellSmall />}
      </DialogContent>

      {selectedSub && (
        <SubscriptionStatusModal
          subscription={selectedSub}
          onClose={() => setSelectedSub(null)}
          onDone={() => setRefreshTick((t) => t + 1)}
        />
      )}
      {retryCharge && (
        <RetryChargeModal
          charge={retryCharge}
          onClose={() => setRetryCharge(null)}
          onDone={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </Dialog>
  );
}
